import React, {useLayoutEffect, useRef} from 'react';
import {useIntl} from 'react-intl';
import {ACTION_BUTTON_SIZE, ActionButton, EMPHASIS, FlexRow, Tooltip} from '@forecast-it/design-system';
import {AGENTIC_WORKERS_MESSAGE} from './agenticWorkersMessages';
import type {AgentDeclaration, AgentPage, AgentRow} from './agentDeclaration';
import {getVisibleAgentsForRow} from './agentRegistry';
import {AGENT_ROW_STATUS, useAgentRowState} from './agentRunState';

const READY_MENU_DATA_CY = 'agent-row-action-ready-menu';

type AgentRowActionProps = {
	declaration: AgentDeclaration;
	row: AgentRow;
};

// The main part and the trailing part are both inside the action button's container.
const isFocusElsewhere = (mainPart: HTMLButtonElement | null): boolean => {
	const focusedElement = document.activeElement;
	return (
		focusedElement !== null &&
		focusedElement !== document.body &&
		!(mainPart?.parentElement?.contains(focusedElement) ?? false)
	);
};

const findReadyMenu = (mainPart: HTMLButtonElement | null): HTMLButtonElement | null =>
	mainPart?.parentElement?.querySelector<HTMLButtonElement>(`[data-cy="${READY_MENU_DATA_CY}"]`) ?? null;

// The menu closes itself after the chosen option runs, and a command that takes the row out of Ready unmounts the menu,
// so every option runs once the menu has closed.
const afterMenuCloses = (command: () => void) => (): void => {
	setTimeout(command);
};

// One agent's control on one row. A component of its own because each agent holds its own run state.
// Every state renders the same main part, so React keeps its button and the focus Stop hands to it.
const AgentRowAction = ({declaration, row}: AgentRowActionProps): JSX.Element => {
	const {formatMessage} = useIntl();
	const {status, reviewedResult, inPageRun, isAccepting, startRun, stopRun, review, accept, runAgain, deleteResult} =
		useAgentRowState(declaration, row);
	const actionRef = useRef<HTMLButtonElement>(null);
	// True from a command chosen in this row's menu until the row shows how it ended, so a change from elsewhere, such as
	// a run starting or someone else acting on the result, never moves focus.
	const awaitsCommandOutcomeRef = useRef(false);
	const previousRef = useRef({status, isAccepting});
	const isWorking = status === AGENT_ROW_STATUS.WORKING;
	const isReady = status === AGENT_ROW_STATUS.READY;

	// The menu part disappears when the row leaves Ready, so focus is handed to the main part instead of falling back to
	// the page. A layout effect, so it has run for every change before the next command is chosen.
	useLayoutEffect(() => {
		const previous = previousRef.current;
		previousRef.current = {status, isAccepting};
		if (!awaitsCommandOutcomeRef.current) {
			return;
		}
		if (previous.status === AGENT_ROW_STATUS.READY && status !== AGENT_ROW_STATUS.READY) {
			awaitsCommandOutcomeRef.current = false;
			if (!isFocusElsewhere(actionRef.current)) {
				actionRef.current?.focus();
			}
			return;
		}
		// An accept leaves the row Ready until its answer.
		if (isAccepting) {
			return;
		}
		awaitsCommandOutcomeRef.current = false;
		// An accept that did not apply leaves the row Ready, and a browser drops focus to the page while the menu part is
		// disabled, so focus is handed back to it.
		if (previous.isAccepting && document.activeElement === document.body) {
			const readyMenu = findReadyMenu(actionRef.current);
			const focusTarget = readyMenu && !readyMenu.disabled ? readyMenu : actionRef.current;
			focusTarget?.focus();
		}
	});

	// Set before the command, which can render the row at once. A command that did not run has no outcome to wait for.
	const handingFocusBack = (command: () => boolean) => (): void => {
		awaitsCommandOutcomeRef.current = true;
		if (!command()) {
			awaitsCommandOutcomeRef.current = false;
		}
	};

	const actionLabel = isWorking
		? formatMessage({id: AGENTIC_WORKERS_MESSAGE.ROW_ACTION_WORKING})
		: isReady
		? formatMessage({id: AGENTIC_WORKERS_MESSAGE.ROW_ACTION_READY})
		: formatMessage(declaration.action);
	const rowActionTip = isWorking
		? declaration.rowActionTips.working
		: isReady
		? declaration.rowActionTips.ready
		: declaration.rowActionTips.idle;
	const stopLabel = formatMessage({
		id: inPageRun ? AGENTIC_WORKERS_MESSAGE.ROW_ACTION_STOP_PAGE_RUN : AGENTIC_WORKERS_MESSAGE.ROW_ACTION_STOP,
	});
	const readyMenuLabel = formatMessage(
		{id: AGENTIC_WORKERS_MESSAGE.ROW_ACTION_READY_MENU},
		{result: formatMessage(declaration.resultNoun.singular)}
	);

	// An idle row reviewed on an earlier visit opens its waiting result again, which brings back Ready and its menu.
	const pressAction = (): void => {
		if (isReady || reviewedResult !== null) {
			review();
			return;
		}
		startRun();
	};

	// The stop part disappears once the run stops, so focus is handed to the main part instead of falling back to the page.
	const stopFromRow = (): void => {
		stopRun();
		actionRef.current?.focus();
	};

	const trailingAction = isWorking
		? {
				icon: 'stop' as const,
				'aria-label': stopLabel,
				tooltip: stopLabel,
				onClick: stopFromRow,
				'data-cy': 'agent-row-action-stop',
		  }
		: isReady
		? {
				icon: 'chevronDown' as const,
				'aria-label': readyMenuLabel,
				tooltip: readyMenuLabel,
				disabled: isAccepting,
				dropdownMenuOptions: [
					{
						name: formatMessage({id: AGENTIC_WORKERS_MESSAGE.RESULT_REVIEW}),
						onClick: afterMenuCloses(review),
					},
					{
						name: formatMessage(declaration.acceptLabel.single),
						onClick: afterMenuCloses(handingFocusBack(accept)),
					},
					{
						name: formatMessage(declaration.runAgainLabel),
						onClick: afterMenuCloses(handingFocusBack(runAgain)),
					},
					{
						name: formatMessage({id: AGENTIC_WORKERS_MESSAGE.RESULT_DELETE}),
						onClick: afterMenuCloses(handingFocusBack(deleteResult)),
					},
				],
				'data-cy': READY_MENU_DATA_CY,
		  }
		: undefined;

	return (
		<FlexRow alignItems={'center'} flex={'0 0 auto'} data-cy={`agent-row-action-${declaration.id}`}>
			<Tooltip content={formatMessage(rowActionTip)}>
				<ActionButton
					ref={actionRef}
					size={ACTION_BUTTON_SIZE.COMPACT}
					emphasis={isReady ? EMPHASIS.HIGH : EMPHASIS.MEDIUM}
					icon={'sparkle'}
					waiting={isWorking || isAccepting}
					trailingAction={trailingAction}
					onClick={pressAction}
					data-cy={'agent-row-action'}
				>
					{actionLabel}
				</ActionButton>
			</Tooltip>
		</FlexRow>
	);
};

type AgentRowActionsProps = {
	page: AgentPage;
	row: AgentRow;
};

export const AgentRowActions = ({page, row}: AgentRowActionsProps): JSX.Element | null => {
	const visibleAgents = getVisibleAgentsForRow(page, row.rowType);
	if (visibleAgents.length === 0) {
		return null;
	}
	return (
		<FlexRow alignItems={'center'} gap={'s'} flex={'0 0 auto'}>
			{visibleAgents.map(declaration => (
				<AgentRowAction key={declaration.id} declaration={declaration} row={row} />
			))}
		</FlexRow>
	);
};
